import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import CONSTANTS from '../../Constants'
import { API_CONFIG, API_LIST } from '../../Constants/ApiDefinations'
import { setLocalStorage } from '../../Helpers/Storage'
import mainApiService from '../../Services'
import { showAlert } from './alert.slice'
import { setLoader } from './loader.slice'

interface NotificationState {
  unreadCount: number
  notificationsList: {
    myUnreadCount: number
    allUnreadCount: number
    count: number
    notifications: any[]
  }
  popupNotificationsList: {
    myUnreadCount: number
    allUnreadCount: number
    count: number
    notifications: any[]
  }
  unReadNotifIds: string[]
}

const initialState: NotificationState = {
  unreadCount: 0,
  notificationsList: {
    myUnreadCount: 0,
    allUnreadCount: 0,
    count: 0,
    notifications: [],
  },
  popupNotificationsList: {
    myUnreadCount: 0,
    allUnreadCount: 0,
    count: 0,
    notifications: [],
  },
  unReadNotifIds: [],
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    setUnreadCount(state, action: PayloadAction<number>) {
      state.unreadCount = action.payload
    },
    setAllNotificationsAsRead(state) {
      state.unreadCount = 0
      state.notificationsList.myUnreadCount = 0
      state.notificationsList.allUnreadCount = 0
      state.notificationsList.notifications.forEach((notification) => {
        notification.isRead = true
      })
      state.popupNotificationsList.myUnreadCount = 0
      state.popupNotificationsList.allUnreadCount = 0
      state.popupNotificationsList.notifications.forEach((notification) => {
        notification.isRead = true
      })
    },
    setNotificationsList(
      state,
      action: PayloadAction<{
        payload: Record<string, any>
        notificationResponse: {
          count: number
          records: any[]
          allUnreadCount: number
          myUnreadCount: number
        }
        isPopup?: boolean
      }>,
    ) {
      const { payload, notificationResponse } = action.payload
      const { apiPayload, isPopup } = payload
      const { count, records: notifications, allUnreadCount, myUnreadCount } = notificationResponse

      const targetList = isPopup ? state.popupNotificationsList : state.notificationsList

      targetList.notifications =
        apiPayload.page === 1 ? notifications : [...targetList.notifications, ...notifications]
      targetList.count = count
      targetList.myUnreadCount = myUnreadCount
      targetList.allUnreadCount = allUnreadCount
    },
    clearNotificationsList(state, action: PayloadAction<{ isPopup?: boolean }>) {
      const clearedData = { myUnreadCount: 0, allUnreadCount: 0, count: 0, notifications: [] }
      if (action.payload.isPopup) {
        state.popupNotificationsList = clearedData
      } else {
        state.notificationsList = clearedData
      }
    },
    setNotificationsAsRead(state, action: PayloadAction<number>) {
      state.unreadCount = Math.max(0, state.unreadCount - action.payload)
    },
    setUnreadNotifIds(state, action: PayloadAction<{ ids: string[]; clearAll?: boolean }>) {
      const { ids, clearAll } = action.payload
      if (clearAll) {
        state.unReadNotifIds = []
        setLocalStorage(CONSTANTS.LOCAL_KEYS.UNREAD_NOTIF_IDS, [])
      } else {
        // Merge previous and new ids, remove duplicates
        const mergedIds = [...state.unReadNotifIds, ...ids]
        state.unReadNotifIds = Array.from(new Set(mergedIds))
        setLocalStorage(CONSTANTS.LOCAL_KEYS.UNREAD_NOTIF_IDS, state.unReadNotifIds)
      }
    },
  },
})

export const {
  setUnreadCount,
  setAllNotificationsAsRead,
  setNotificationsList,
  clearNotificationsList,
  setNotificationsAsRead,
  setUnreadNotifIds,
} = notificationsSlice.actions

interface GetNotificationsListPayload {
  apiPayload: {
    limit?: number
    page?: number
    myNotification?: boolean
    notificationFilter?: Array<{
      filterBy: string
      filterCondition: string
      filterValue: any[] | string | boolean
    }>
    onlyUnreadCount?: boolean
  }
  showLoader?: boolean
  isPopup?: boolean
}

interface NotificationResponse {
  responseCode: number
  data: {
    myUnreadCount: number
    allUnreadCount: number
    count: number
    records: any[]
  }
}

export const getNotifications = createAsyncThunk(
  'notifications/getNotifications',
  async (payload: GetNotificationsListPayload, { dispatch }) => {
    if (payload.showLoader) {
      dispatch(setLoader(true))
    }
    const requestedData: any = API_CONFIG(API_LIST.NOTIFICATIONS.GET, payload.apiPayload)
    const apiResponse = await mainApiService(requestedData)
    const notificationResponse: NotificationResponse = {
      responseCode: apiResponse.responseCode || 0,
      data: apiResponse.data || { count: 0, records: [] },
    }
    if (notificationResponse?.responseCode === 200) {
      if (payload.apiPayload.onlyUnreadCount) {
        dispatch(setUnreadCount(notificationResponse.data.count))
        return notificationResponse
      }
      dispatch(
        setNotificationsList({
          payload,
          notificationResponse: notificationResponse.data,
        }),
      )
      // const unreadCount = notificationResponse.data.records.filter(
      //   (notification) => !notification.isRead,
      // )
      // if (unreadCount.length > 0) {
      //   dispatch(
      //     updateNotificationsReadStatus({
      //       apiPayload: { isRead: true, notificationIds: unreadCount.map((n) => n._id) },
      //       showLoader: false,
      //     }),
      //   )
      // }
    } else {
      dispatch(showAlert([true, CONSTANTS.MESSAGES.API_ERROR, 'error']))
    }
    if (payload.showLoader) {
      dispatch(setLoader(false))
    }
    return notificationResponse
  },
)

interface UpdateNotificationsListPayload {
  apiPayload: {
    isRead?: boolean
    isReadAll?: boolean
    notificationIds?: string[]
  }
  showLoader?: boolean
}

export const updateNotificationsReadStatus = createAsyncThunk(
  'notifications/updateNotificationsReadStatus',
  async (payload: UpdateNotificationsListPayload, { dispatch }) => {
    if (payload.showLoader) {
      dispatch(setLoader(true))
    }
    const requestedData: any = API_CONFIG(API_LIST.NOTIFICATIONS.MARK_AS_READ, payload.apiPayload)
    const apiResponse = await mainApiService(requestedData)
    if (apiResponse?.responseCode === 200) {
      if (payload.apiPayload.isReadAll) {
        dispatch(setAllNotificationsAsRead()) // Reset unread count if all are marked as read
      } else {
        dispatch(
          setNotificationsAsRead(
            apiResponse.data?.modifiedCount || payload.apiPayload.notificationIds?.length || 0,
          ),
        )
      }
    }
    if (payload.showLoader) {
      dispatch(setLoader(false))
    }
    return apiResponse
  },
)

interface GetNonDeliveredNotificationsPayload {
  apiPayload?: {
    limit: number
    userId?: string
    markRestAsRead?: boolean
    markFetchedAsDelivered?: boolean
    minDate?: string
  }
  showLoader?: boolean
}

export const getNonDeliveredNotifications = createAsyncThunk(
  'notifications/getNonDeliveredNotifications',
  async (payload: GetNonDeliveredNotificationsPayload, { dispatch }) => {
    if (payload.showLoader) {
      dispatch(setLoader(true))
    }
    const { limit, userId, markFetchedAsDelivered, minDate } = payload.apiPayload || {}
    const query: any = [
      `limit=${limit}`,
      userId ? `userId=${userId}` : '',
      minDate ? `minDate=${minDate}` : '',
      markFetchedAsDelivered !== undefined
        ? `markFetchedAsDelivered=${markFetchedAsDelivered}`
        : '',
    ]
      .filter(Boolean)
      .join('&')
      .replace(/^/, '')
    const apiResponse = await mainApiService(
      API_CONFIG(API_LIST.NOTIFICATIONS.GET_NON_DELIVERED_NOTIFICATIONS, {}, null, query),
    )
    if (payload.showLoader) {
      dispatch(setLoader(false))
    }
    return apiResponse
  },
)

export default notificationsSlice
